import React, { useState, useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { useCreateScheduleMutation, useUpdateScheduleMutation } from 'src/api/mutations/schedulesMutations';
import { useScheduleQuery } from 'src/api/queries/schedulesQueries';
import { dryRunSchedule } from 'src/api/functions/scheduleRunsApi';
import { useAiSchedulesUnavailable } from 'src/hooks/useAiSchedulesUnavailable';
import { useWritesPaused } from 'src/hooks/useWritesPaused';
import { PageTitle, Button, FeatureUnavailable, WritesPausedBanner } from 'src/components';
import { AuthorizeBanner } from './components/AuthorizeBanner';
import { RecurrencePicker } from './components/RecurrencePicker';
import { TimezoneSelect } from './components/TimezoneSelect';
import { DestructiveToggle } from './components/DestructiveToggle';
import { SsePreviewPanel } from './components/SsePreviewPanel';
import type { RecurrenceKind, DryRunPayload, CreateSchedulePayload, UpdateSchedulePayload } from 'src/types/Schedules';
import styles from './ScheduleForm.module.scss';

const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface Errors {
  title?: string;
  prompt?: string;
  daysOfWeek?: string;
  server?: string;
}

export const ScheduleForm: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const scheduleFeatureUnavailable = useAiSchedulesUnavailable();
  // Reachable by URL even when the list disables "New agent": save, preview
  // and authorize would all 503 on a write-blocked account (#1215).
  const { paused: writesPaused, reason: pausedReason } = useWritesPaused();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const prefillPrompt = queryParams.get('prompt') || '';
  const prefillTitle = queryParams.get('title') || '';

  const { data: existing, isLoading: loadingExisting } = useScheduleQuery(id);

  const [title, setTitle] = useState(existing?.title || prefillTitle);
  const [prompt, setPrompt] = useState(existing?.prompt || prefillPrompt);
  const [kind, setKind] = useState<RecurrenceKind>(existing?.recurrence.kind || 'daily');
  const [time, setTime] = useState(existing?.recurrence.time || '07:00');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(existing?.recurrence.days_of_week || [1, 2, 3, 4, 5]);
  const [dayOfMonth, setDayOfMonth] = useState(existing?.recurrence.day_of_month || 1);
  const [timezone, setTimezone] = useState(existing?.recurrence.timezone || browserTz);
  const [allowDestructive, setAllowDestructive] = useState(existing?.allow_destructive || false);
  const [maxDuration, setMaxDuration] = useState(existing?.max_duration_s || 300);
  const [errors, setErrors] = useState<Errors>({});
  const [showPreview, setShowPreview] = useState(false);

  const accessToken = useAccessToken();
  const { showSuccess } = useAlerts();

  const createMutation = useCreateScheduleMutation({
    onSuccess: (result) => {
      showSuccess('Agent created');
      history.push(`/ai/schedules/${result.id}`);
    },
    onError: (err: any) => {
      const errObj = err?.error || err;
      if (typeof errObj === 'string' && errObj.includes('schedule_limit_exceeded')) {
        setErrors((e) => ({ ...e, server: 'You have reached the maximum number of active agents (20). Delete or pause one first.' }));
      } else if (typeof errObj === 'string' && errObj.includes('invalid_recurrence')) {
        setErrors((e) => ({ ...e, server: 'Recurrence interval is too short. Minimum gap is 15 minutes.' }));
      } else {
        setErrors((e) => ({ ...e, server: err?.message || 'Failed to create agent' }));
      }
    },
  });

  const updateMutation = useUpdateScheduleMutation(id || '', {
    onSuccess: () => {
      showSuccess('Agent updated');
      history.push(`/ai/schedules/${id}`);
    },
    onError: (err: any) => {
      const errObj = err?.error || err;
      if (typeof errObj === 'string' && errObj.includes('invalid_recurrence')) {
        setErrors((e) => ({ ...e, server: 'Recurrence interval is too short. Minimum gap is 15 minutes.' }));
      } else {
        setErrors((e) => ({ ...e, server: err?.message || 'Failed to update agent' }));
      }
    },
  });

  // Sync edit data when loaded
  React.useEffect(() => {
    if (existing && isEdit) {
      setTitle(existing.title);
      setPrompt(existing.prompt);
      setKind(existing.recurrence.kind);
      setTime(existing.recurrence.time);
      setDaysOfWeek(existing.recurrence.days_of_week || [1, 2, 3, 4, 5]);
      setDayOfMonth(existing.recurrence.day_of_month || 1);
      setTimezone(existing.recurrence.timezone || browserTz);
      setAllowDestructive(existing.allow_destructive);
      setMaxDuration(existing.max_duration_s);
    }
  }, [existing, isEdit]);

  const validate = (): boolean => {
    const next: Errors = {};
    if (!title.trim() || title.length > 200) next.title = 'Title is required (1–200 characters)';
    if (!prompt.trim() || prompt.length > 8000) next.prompt = 'Prompt is required (1–8000 characters)';
    if (kind === 'weekly' && daysOfWeek.length === 0) next.daysOfWeek = 'Select at least one day';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildRecurrence = () => ({
    kind,
    time,
    timezone,
    ...(kind === 'weekly' ? { days_of_week: daysOfWeek } : {}),
    ...(kind === 'monthly' ? { day_of_month: dayOfMonth } : {}),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const recurrence = buildRecurrence();
    if (isEdit) {
      const payload: UpdateSchedulePayload = {
        title: title.trim(),
        prompt: prompt.trim(),
        recurrence,
        timezone,
        allow_destructive: allowDestructive,
        max_duration_s: Math.min(maxDuration, 600),
      };
      updateMutation.mutate(payload);
    } else {
      const payload: CreateSchedulePayload = {
        title: title.trim(),
        prompt: prompt.trim(),
        recurrence,
        timezone,
        allow_destructive: allowDestructive,
        max_duration_s: Math.min(maxDuration, 600),
      };
      createMutation.mutate(payload);
    }
  };

  const handlePreview = () => {
    if (!validate()) return;
    setShowPreview(true);
  };

  const dryRunRequest = useCallback(
    (signal: AbortSignal) => {
      const payload: DryRunPayload = {
        title: title.trim(),
        prompt: prompt.trim(),
        recurrence: buildRecurrence(),
        allow_destructive: allowDestructive,
        max_duration_s: Math.min(maxDuration, 600),
      };
      return dryRunSchedule(accessToken, payload, signal);
    },
    [accessToken, title, prompt, kind, time, timezone, daysOfWeek, dayOfMonth, allowDestructive, maxDuration],
  );

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  if (scheduleFeatureUnavailable) {
    return <FeatureUnavailable title="Scheduled agents" />;
  }

  if (isEdit && loadingExisting) {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle
          title="Scheduled agents"
          subtitle={isEdit ? 'Edit agent' : 'New agent'}
          className={styles.pageTitle}
        />
        <div className={styles.pageContent}>
          <div className={styles.loading}>Loading agent…</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <PageTitle
        title="Scheduled agents"
        subtitle={isEdit ? 'Edit agent' : 'New agent'}
        className={styles.pageTitle}
        rightContainer={
          <Button size="small" mode="tertiary" onClick={() => history.goBack()}>
            ← Back
          </Button>
        }
      />

      <div className={styles.pageContent}>
        {writesPaused && <WritesPausedBanner reason={pausedReason} />}
        <AuthorizeBanner disabled={writesPaused} />

        <div className={styles.layout}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Title</label>
              <input
                className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Daily approvals digest"
                maxLength={200}
              />
              {errors.title && <span className={styles.errorText}>{errors.title}</span>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Prompt</label>
              <textarea
                className={`${styles.textarea} ${errors.prompt ? styles.inputError : ''}`}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="What should the agent do on each run?"
                rows={4}
                maxLength={8000}
              />
              <div className={styles.charCount}>{prompt.length} / 8000</div>
              {errors.prompt && <span className={styles.errorText}>{errors.prompt}</span>}
            </div>

            <RecurrencePicker
              kind={kind}
              time={time}
              daysOfWeek={daysOfWeek}
              dayOfMonth={dayOfMonth}
              onKindChange={setKind}
              onTimeChange={setTime}
              onDaysOfWeekChange={setDaysOfWeek}
              onDayOfMonthChange={setDayOfMonth}
            />
            {errors.daysOfWeek && <span className={styles.errorText}>{errors.daysOfWeek}</span>}

            <TimezoneSelect value={timezone} onChange={setTimezone} />

            <div className={styles.fieldGroup}>
              <label className={styles.label}>Max duration (seconds)</label>
              <input
                type="number"
                className={styles.input}
                value={maxDuration}
                min={30}
                max={600}
                onChange={(e) => setMaxDuration(Math.min(600, Math.max(30, parseInt(e.target.value) || 300)))}
              />
              <span className={styles.hint}>Maximum 600 seconds (10 minutes)</span>
            </div>

            <DestructiveToggle value={allowDestructive} onChange={setAllowDestructive} />

            {errors.server && <div className={styles.serverError}>{errors.server}</div>}

            <div className={styles.actions}>
              <Button type="submit" size="small" disabled={writesPaused || isSaving} loading={isSaving}>
                {isSaving ? 'Saving…' : isEdit ? 'Update agent' : 'Save agent'}
              </Button>
              <Button type="button" size="small" mode="tertiary" onClick={handlePreview} disabled={writesPaused || isSaving}>
                Preview (dry run)
              </Button>
              <Button type="button" size="small" mode="flat" onClick={() => history.goBack()}>
                Cancel
              </Button>
            </div>
          </form>

          {showPreview && (
            <div className={styles.previewColumn}>
              <SsePreviewPanel
                title="Dry run preview"
                sendRequest={dryRunRequest}
                onClose={() => setShowPreview(false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
